import { LoaderFunction } from 'react-router-dom';
import { ContentType } from '@atlassian/learning-components';

import { ContentfulSmCourse, ContentfulSmCourseResponse, GET_SM_COURSE } from 'src/graphql/queries/GetSmCourse';
import { GetCourseQueryVariables } from 'src/graphql/generated/GetSmCourse';
import { GetLearningPathIdQuery, GetLearningPathIdQueryVariables } from 'src/graphql/generated/GetLearningPathId';
import { fetchGraphQL } from 'src/services/contentful/client';
import { GET_LEARNING_PATH_ID, GetLearningPathId } from 'src/graphql/queries/GetLearningPathId';
import { getNotificationsById, NotificationsMappedResponse } from 'src/services/http/notifications';
import { mapCourseNotificationsData, sortNotificationsByDate } from 'src/utils/transformNotificationData';
import {
  ContentfulLearningPathBasic,
  GET_LEARNING_PATHS_BY_COURSE_SLUG,
  GetLearningPathsByCourseSlugQuery,
  GetLearningPathsByCourseSlugQueryVariables,
} from 'src/graphql/queries/GetLearningPathsByCourseSlug';
import { isPreviewMode } from 'src/utils/isPreviewMode';

export const loader: LoaderFunction = async ({ params }): Promise<CourseLoaderProps> => {
  const courseId = params.courseId ?? '';
  const pathId = params.pathId ?? '';
  const preview = isPreviewMode();

  const course = await getCourse(courseId, preview);
  const learningPath = await getLearningPathId(pathId, preview);
  const learningPathsByCourse = await getLearningPathsByCourseSlug(courseId, preview);

  if (!course) {
    throw new Error('Course was not found');
  }

  const courseNotifications = await getNotificationsById(course.sys.id, ContentType.COURSE);
  const sortedUnseenNotifications = sortNotificationsByDate(courseNotifications?.unseenNotifications);
  const sortedHistoricalNotifications = sortNotificationsByDate(courseNotifications?.historicalNotifications);
  const mappedUnseenNotifications = mapCourseNotificationsData(sortedUnseenNotifications, course);
  const mappedHistoricalNotifications = mapCourseNotificationsData(sortedHistoricalNotifications, course);

  return {
    course,
    learningPath,
    learningPathsByCourse,
    notifications: {
      unseenNotifications: mappedUnseenNotifications,
      historicalNotifications: mappedHistoricalNotifications,
    },
  };
};

const getCourse = async (courseId: string, preview: boolean): Promise<ContentfulSmCourse | undefined> => {
  const response = await fetchGraphQL<ContentfulSmCourseResponse, GetCourseQueryVariables>(GET_SM_COURSE, {
    slug: courseId,
    preview,
  });

  return response.smCourseCollection.items[0];
};

const getLearningPathId = async (courseId: string, preview: boolean): Promise<GetLearningPathId | undefined> => {
  const response = await fetchGraphQL<GetLearningPathIdQuery, GetLearningPathIdQueryVariables>(GET_LEARNING_PATH_ID, {
    slug: courseId,
    preview,
  });

  return response.learningPathCollection.items[0];
};

const getLearningPathsByCourseSlug = async (
  courseId: string,
  preview: boolean,
): Promise<ContentfulLearningPathBasic> => {
  const response = await fetchGraphQL<GetLearningPathsByCourseSlugQuery, GetLearningPathsByCourseSlugQueryVariables>(
    GET_LEARNING_PATHS_BY_COURSE_SLUG,
    {
      slug: courseId,
      preview,
    },
  );

  return response.learningPathCollection.items;
};

export interface CourseLoaderProps {
  course: ContentfulSmCourse;
  learningPath?: GetLearningPathId;
  notifications: NotificationsMappedResponse;
  learningPathsByCourse?: ContentfulLearningPathBasic;
}
